/**
 * Client-side bank-detail validation for SEPA forms.
 *
 * We deliberately avoid a schema library (zod) here — these helpers are tiny,
 * dependency-free, and the IBAN mod-97 check is a stronger guarantee than any
 * regex. Used by the mandate / einmal-Lastschrift / refund dialogs.
 */

/** Strips spaces and upper-cases an IBAN for storage/validation. */
export function normalizeIban(iban: string): string {
  return (iban || "").replace(/\s+/g, "").toUpperCase();
}

/** Groups an IBAN into blocks of four for display, e.g. "DE89 3704 0044 …". */
export function formatIbanForDisplay(iban: string): string {
  return normalizeIban(iban)
    .replace(/(.{4})/g, "$1 ")
    .trim();
}

/** Masks the middle of an IBAN for compact display, e.g. "DE89 •••• 3000". */
export function maskIbanForDisplay(iban: string): string {
  const normalized = normalizeIban(iban);
  if (normalized.length <= 8) return formatIbanForDisplay(normalized);
  return `${normalized.slice(0, 4)} •••• ${normalized.slice(-4)}`;
}

/** ISO 3166 country code -> expected total IBAN length. */
const IBAN_LENGTHS: Record<string, number> = {
  AD: 24,
  AE: 23,
  AL: 28,
  AT: 20,
  AZ: 28,
  BA: 20,
  BE: 16,
  BG: 22,
  BH: 22,
  BR: 25,
  BY: 28,
  CH: 21,
  CR: 22,
  CY: 28,
  CZ: 24,
  DE: 22,
  DK: 18,
  DO: 28,
  EE: 20,
  EG: 29,
  ES: 24,
  FI: 18,
  FO: 18,
  FR: 27,
  GB: 22,
  GE: 22,
  GI: 23,
  GL: 18,
  GR: 27,
  GT: 28,
  HR: 21,
  HU: 28,
  IE: 22,
  IL: 23,
  IS: 26,
  IT: 27,
  JO: 30,
  KW: 30,
  KZ: 20,
  LB: 28,
  LC: 32,
  LI: 21,
  LT: 20,
  LU: 20,
  LV: 21,
  MC: 27,
  MD: 24,
  ME: 22,
  MK: 19,
  MR: 27,
  MT: 31,
  MU: 30,
  NL: 18,
  NO: 15,
  PK: 24,
  PL: 28,
  PS: 29,
  PT: 25,
  QA: 29,
  RO: 24,
  RS: 22,
  SA: 24,
  SE: 24,
  SI: 19,
  SK: 24,
  SM: 27,
  TN: 24,
  TR: 26,
  UA: 29,
  VG: 24,
  XK: 20,
};

/** ISO 7064 MOD-97-10 remainder for a normalised IBAN (first 4 chars moved to the end). */
function ibanMod97(iban: string): number {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  let remainder = 0;
  for (const char of rearranged) {
    // Letters expand to two-digit numbers: A=10 ... Z=35.
    const chunk = char >= "A" && char <= "Z" ? String(char.charCodeAt(0) - 55) : char;
    for (const digit of chunk) {
      remainder = (remainder * 10 + Number(digit)) % 97;
    }
  }
  return remainder;
}

/**
 * Validates an IBAN by its country-specific length and the ISO 7064 MOD-97-10
 * checksum. Input is normalised first, so spaces / lower-case are accepted.
 */
export function isValidIban(iban: string | undefined | null): boolean {
  const normalized = normalizeIban(iban ?? "");
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/.test(normalized)) return false;

  const expectedLength = IBAN_LENGTHS[normalized.slice(0, 2)];
  if (!expectedLength || normalized.length !== expectedLength) return false;

  return ibanMod97(normalized) === 1;
}

export interface BankDetailsInput {
  holderFirstName?: string;
  holderLastName?: string;
  iban?: string;
  bic?: string;
}

/**
 * Validates inline bank details: presence of holder name, and a structurally
 * valid IBAN (country length + MOD-97 checksum). Returns a map of
 * field -> German error message; an empty object means the input is valid.
 */
export function validateBankDetails(input: BankDetailsInput): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!input.holderFirstName?.trim()) {
    errors.holderFirstName = "Vorname erforderlich";
  }
  if (!input.holderLastName?.trim()) {
    errors.holderLastName = "Nachname erforderlich";
  }
  if (!input.iban?.trim()) {
    errors.iban = "IBAN erforderlich";
  } else if (!isValidIban(input.iban)) {
    errors.iban = "IBAN ungültig (Länge oder Prüfsumme)";
  }
  return errors;
}
